import { useState, useEffect } from 'react';
import { apiFetch } from '../../../lib/api';

export interface Step2Data {
  environments:    Array<{ name: string; purpose: string }>;
  promotionModel:  'AUTO_ON_MERGE' | 'MANUAL_PROMOTION' | 'APPROVAL_GATE';
  ciProvider:      'GITHUB_ACTIONS' | 'GITLAB_CI' | 'JENKINS' | 'CUSTOM';
  branchStrategy:  'TRUNK' | 'GITFLOW' | 'CUSTOM';
  requiresRunbook: boolean;
  requiresCAB:     boolean;
}

interface Props {
  tenantId:       string;
  /** Seed values (wizard passes prior-step state). When omitted the form loads
   *  the existing policy on mount, falling back to DEFAULT_DATA. */
  savedData?:     Step2Data;
  /** Label for the submit button. Wizard: "Save & Continue →"; settings: "Save changes". */
  submitLabel?:   string;
  /** Called after a successful POST. The wizard wires this to its step-advance
   *  handler; settings surfaces can use it as a post-save hook (or rely on the
   *  internal saved flash). */
  onSaved?:       (data: Step2Data) => void;
  /** When false the form is read-only (no edits, no submit). Settings passes
   *  isAdmin. The server's requireRole('TENANT_ADMIN') on POST is the
   *  authoritative gate; this enforces the same invariant in the UI. */
  canEdit?:       boolean;
  /** When true (default) renders the "Deployment Policy" heading + intro. Pages
   *  with their own heading (settings) pass false to avoid a duplicate title. */
  showIntro?:     boolean;
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--surface-alt)',
  color: 'var(--text)',
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
};

const fieldGap: React.CSSProperties = { marginBottom: 20 };

function RadioGroup({
  name,
  options,
  value,
  onChange,
  disabled,
}: {
  name: string;
  options: Array<{ value: string; label: string; description?: string }>;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      {options.map(opt => (
        <label
          key={opt.value}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            padding: '9px 16px',
            borderRadius: 'var(--radius-sm)',
            border: `1px solid ${value === opt.value ? 'var(--primary)' : 'var(--border)'}`,
            background: value === opt.value ? 'rgba(124,58,237,0.06)' : 'var(--surface-alt)',
            cursor: disabled ? 'default' : 'pointer',
            opacity: disabled && value !== opt.value ? 0.7 : 1,
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            fontWeight: value === opt.value ? 600 : 400,
            color: value === opt.value ? 'var(--primary)' : 'var(--text)',
            transition: 'border-color 150ms, background 150ms',
          }}
        >
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            disabled={disabled}
            onChange={() => onChange(opt.value)}
            style={{ accentColor: 'var(--primary)', marginTop: 2 }}
          />
          <span>
            {opt.label}
            {opt.description && (
              <span style={{ display: 'block', fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)', marginTop: 2 }}>
                {opt.description}
              </span>
            )}
          </span>
        </label>
      ))}
    </div>
  );
}

function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        borderRadius: 'var(--radius-sm)',
        border: `1px solid ${checked ? 'var(--primary)' : 'var(--border)'}`,
        background: checked ? 'rgba(124,58,237,0.06)' : 'var(--surface-alt)',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'border-color 150ms, background 150ms',
      }}
    >
      <div
        onClick={() => { if (!disabled) onChange(!checked); }}
        style={{
          width: 40,
          height: 22,
          borderRadius: 11,
          background: checked ? 'var(--primary)' : 'var(--border)',
          position: 'relative',
          flexShrink: 0,
          cursor: disabled ? 'default' : 'pointer',
          transition: 'background 150ms',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 3,
            left: checked ? 21 : 3,
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: '#fff',
            transition: 'left 150ms',
            boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
          }}
        />
      </div>
      <div>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 500, color: 'var(--text)', margin: 0 }}>
          {label}
        </p>
        {description && (
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: '2px 0 0' }}>
            {description}
          </p>
        )}
      </div>
    </label>
  );
}

const DEFAULT_DATA: Step2Data = {
  environments:    [{ name: '', purpose: '' }],
  promotionModel:  'MANUAL_PROMOTION',
  ciProvider:      'GITHUB_ACTIONS',
  branchStrategy:  'TRUNK',
  requiresRunbook: false,
  requiresCAB:     false,
};

export default function Step2DeploymentPolicy({
  tenantId,
  savedData,
  submitLabel = 'Save & Continue →',
  onSaved,
  canEdit = true,
  showIntro = true,
}: Props) {
  const [envCount,        setEnvCount]        = useState(savedData?.environments.length ?? 1);
  const [environments,    setEnvironments]    = useState<Array<{ name: string; purpose: string }>>(
    savedData?.environments ?? [{ name: '', purpose: '' }],
  );
  const [promotionModel,  setPromotionModel]  = useState<Step2Data['promotionModel']>(
    savedData?.promotionModel ?? DEFAULT_DATA.promotionModel,
  );
  const [ciProvider,      setCiProvider]      = useState<Step2Data['ciProvider']>(
    savedData?.ciProvider ?? DEFAULT_DATA.ciProvider,
  );
  const [branchStrategy,  setBranchStrategy]  = useState<Step2Data['branchStrategy']>(
    savedData?.branchStrategy ?? DEFAULT_DATA.branchStrategy,
  );
  const [requiresRunbook, setRequiresRunbook] = useState(savedData?.requiresRunbook ?? false);
  const [requiresCAB,     setRequiresCAB]     = useState(savedData?.requiresCAB ?? false);

  const [saving,     setSaving]     = useState(false);
  const [saveError,  setSaveError]  = useState('');
  const [savedFlash, setSavedFlash] = useState(false);

  // Load existing policy on mount
  useEffect(() => {
    apiFetch<Step2Data & { environments: Array<{ name: string; purpose: string }> }>(
      `/tenants/${tenantId}/deployment-policy`,
    )
      .then(policy => {
        const envs = Array.isArray(policy.environments) ? policy.environments : [];
        setEnvCount(envs.length || 1);
        setEnvironments(envs.length ? envs : [{ name: '', purpose: '' }]);
        setPromotionModel(policy.promotionModel);
        setCiProvider(policy.ciProvider);
        setBranchStrategy(policy.branchStrategy);
        setRequiresRunbook(policy.requiresRunbook);
        setRequiresCAB(policy.requiresCAB);
      })
      .catch(() => {}); // no policy yet — fine
  }, [tenantId]);

  // Sync environments array length with envCount
  const handleEnvCountChange = (n: number) => {
    const count = Math.max(1, Math.min(10, n));
    setEnvCount(count);
    setEnvironments(prev => {
      const next = [...prev];
      while (next.length < count) next.push({ name: '', purpose: '' });
      return next.slice(0, count);
    });
  };

  const updateEnv = (index: number, field: 'name' | 'purpose', value: string) => {
    setEnvironments(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const focusRing = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.boxShadow  = '0 0 0 3px rgba(124,58,237,0.12)';
    e.currentTarget.style.borderColor = 'var(--primary)';
  };
  const blurRing = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.boxShadow  = 'none';
    e.currentTarget.style.borderColor = 'var(--border)';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Read-only guard: the submit button is hidden when canEdit=false, but a
    // form submit can still be triggered programmatically. The authoritative
    // gate is the server's requireRole('TENANT_ADMIN') on POST; this keeps the
    // UI's read-only invariant true at the JS layer too.
    if (!canEdit) return;
    setSaving(true);
    setSaveError('');
    setSavedFlash(false);
    try {
      const body: Step2Data = {
        environments,
        promotionModel,
        ciProvider,
        branchStrategy,
        requiresRunbook,
        requiresCAB,
      };

      await apiFetch(`/tenants/${tenantId}/deployment-policy`, {
        method: 'POST',
        body:   JSON.stringify(body),
      });

      setSavedFlash(true);
      onSaved?.(body);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save deployment policy');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={e => void handleSubmit(e)}>
      {showIntro && (
        <>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
            Deployment Policy
          </h3>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 28 }}>
            Define your deployment environments, CI pipeline, and release controls.
          </p>
        </>
      )}

      {/* Environment Count */}
      <div style={fieldGap}>
        <label style={labelStyle}>
          Number of environments <span style={{ color: 'var(--error)' }}>*</span>
        </label>
        <input
          type="number"
          min={1}
          max={10}
          value={envCount}
          disabled={!canEdit}
          onChange={e => handleEnvCountChange(Number(e.target.value))}
          style={{ ...inputStyle, width: 100 }}
          onFocus={focusRing}
          onBlur={blurRing}
        />
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
          How many deployment environments does your project use?
        </p>
      </div>

      {/* Environment rows */}
      <div style={fieldGap}>
        <label style={labelStyle}>Environment configuration <span style={{ color: 'var(--error)' }}>*</span></label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {environments.map((env, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <input
                type="text"
                value={env.name}
                disabled={!canEdit}
                onChange={e => updateEnv(i, 'name', e.target.value)}
                placeholder={`Environment ${i + 1} name (e.g. staging)`}
                style={inputStyle}
                onFocus={focusRing}
                onBlur={blurRing}
              />
              <input
                type="text"
                value={env.purpose}
                disabled={!canEdit}
                onChange={e => updateEnv(i, 'purpose', e.target.value)}
                placeholder="Purpose (e.g. QA testing)"
                style={inputStyle}
                onFocus={focusRing}
                onBlur={blurRing}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Promotion Model */}
      <div style={fieldGap}>
        <label style={labelStyle}>Promotion model <span style={{ color: 'var(--error)' }}>*</span></label>
        <RadioGroup
          name="promotionModel"
          value={promotionModel}
          onChange={v => setPromotionModel(v as Step2Data['promotionModel'])}
          disabled={!canEdit}
          options={[
            { value: 'AUTO_ON_MERGE',    label: 'Auto on merge',    description: 'Deploy automatically when branch merges' },
            { value: 'MANUAL_PROMOTION', label: 'Manual promotion', description: 'Team triggers each promotion' },
            { value: 'APPROVAL_GATE',    label: 'Approval gate',    description: 'Requires explicit sign-off to promote' },
          ]}
        />
      </div>

      {/* CI Provider */}
      <div style={fieldGap}>
        <label style={labelStyle}>CI provider <span style={{ color: 'var(--error)' }}>*</span></label>
        <RadioGroup
          name="ciProvider"
          value={ciProvider}
          onChange={v => setCiProvider(v as Step2Data['ciProvider'])}
          disabled={!canEdit}
          options={[
            { value: 'GITHUB_ACTIONS', label: 'GitHub Actions' },
            { value: 'GITLAB_CI',      label: 'GitLab CI' },
            { value: 'JENKINS',        label: 'Jenkins' },
            { value: 'CUSTOM',         label: 'Custom' },
          ]}
        />
      </div>

      {/* Branch Strategy */}
      <div style={fieldGap}>
        <label style={labelStyle}>Branch strategy <span style={{ color: 'var(--error)' }}>*</span></label>
        <RadioGroup
          name="branchStrategy"
          value={branchStrategy}
          onChange={v => setBranchStrategy(v as Step2Data['branchStrategy'])}
          disabled={!canEdit}
          options={[
            { value: 'TRUNK',   label: 'Trunk-based',  description: 'Short-lived branches, frequent merges to main' },
            { value: 'GITFLOW', label: 'Gitflow',       description: 'Dedicated develop, release, and hotfix branches' },
            { value: 'CUSTOM',  label: 'Custom',        description: 'Custom branching convention' },
          ]}
        />
      </div>

      {/* Toggles */}
      <div style={{ ...fieldGap, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <label style={labelStyle}>Release controls</label>
        <Toggle
          checked={requiresRunbook}
          onChange={setRequiresRunbook}
          disabled={!canEdit}
          label="Deployment runbook required"
          description="Every deployment must have an associated runbook document"
        />
        <Toggle
          checked={requiresCAB}
          onChange={setRequiresCAB}
          disabled={!canEdit}
          label="CAB approval required"
          description="Change Advisory Board must approve releases before deployment"
        />
      </div>

      {/* Error banner */}
      {saveError && (
        <div style={{
          marginBottom: 16, padding: '10px 12px',
          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 13,
          color: 'var(--error)',
        }}>
          {saveError}
        </div>
      )}

      {/* Saved flash */}
      {savedFlash && !saveError && (
        <div style={{
          marginBottom: 16, padding: '10px 12px',
          background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)',
          borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 13,
          color: '#10b981',
        }}>
          Deployment policy saved.
        </div>
      )}

      {/* Submit — hidden in read-only mode */}
      {canEdit ? (
        <button
          type="submit"
          disabled={saving}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            fontWeight: 600,
            padding: '10px 24px',
            background: 'var(--primary)',
            color: '#fff',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? 'Saving…' : submitLabel}
        </button>
      ) : (
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
          Only tenant admins can edit the deployment policy.
        </p>
      )}
    </form>
  );
}
